import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { useResumeData } from '@/hooks/commercial/use-resume-data';
import { useResumeValidation } from '@/hooks/commercial/use-resume-validation';
import { calculateCartSummaryCategories } from '@/utils/commercial/cart-summary-calculations';
import { ResumeFooterActions } from './resume/resume-actions-panel';
import { ResumeContactAddressSection } from './resume/resume-contact-address-section';
import { ResumeItemsSummaryCard } from './resume/resume-items-summary-card';
import { ResumeNegotiationInfoCard } from './resume/resume-negotiation-info-card';
import { ResumePaymentConditionsCard } from './resume/resume-payment-conditions-card';
import { ResumeSellerSection } from './resume/resume-seller-section';

interface StepResumeProps {
  proposalId?: string;
  clientId?: string;
  onUpdate?: () => void;
}

export function StepResume({ proposalId, clientId, onUpdate }: StepResumeProps) {
  const {
    proposal,
    payment,
    cartItems,
    calculateData,
    contacts,
    deliveryAddress,
    creditAnalysisResolution,
    isLoading,
  } = useResumeData(proposalId, clientId);

  const summaryPaymentType = payment?.paymentType ?? 'CASH';
  const cartSummaryCategories = useMemo(
    () => calculateCartSummaryCategories(cartItems, summaryPaymentType),
    [cartItems, summaryPaymentType],
  );

  const totalDiscountInCents = calculateData?.paymentSummary?.totalDiscountInCents ?? 0;

  const validation = useResumeValidation({
    proposalStatus: proposal?.status,
    hasCartItems: cartItems.length > 0,
    hasPayment: !!payment?.paymentType,
    hasDeliveryAddress: !!deliveryAddress,
    hasFiscalContext: proposal?.deliveryMode !== 'BWS' || !!proposal.fiscalAddressId,
    cartItems,
    creditAnalysisResolution,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      {/* Seller */}
      <ResumeSellerSection sellerName={proposal?.userName} sellerId={proposal?.userId} />

      {/* Contact & Delivery */}
      <ResumeContactAddressSection
        contacts={contacts}
        deliveryAddress={deliveryAddress}
        deliveryMode={proposal?.deliveryMode ?? 'CLIENT'}
        pickupLocationName={proposal?.pickupAddressSnapshot?.name}
      />

      {/* Items & Values - Negotiation info card */}
      <ResumeNegotiationInfoCard
        payment={payment}
        paymentSummary={calculateData?.paymentSummary ?? null}
      />

      {/* Items summary and payment conditions */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <ResumeItemsSummaryCard
          items={cartItems}
          proposalId={proposalId}
          paymentType={payment?.paymentType}
        />
        <ResumePaymentConditionsCard
          payment={payment}
          breakdown={calculateData?.breakdown ?? null}
          cartSummaryCategories={cartSummaryCategories}
          paymentSummary={calculateData?.paymentSummary ?? null}
          totalDiscountInCents={totalDiscountInCents}
        />
      </div>

      {/* Footer Actions */}
      <ResumeFooterActions
        proposalId={proposalId}
        clientName={proposal?.clientName}
        status={proposal?.status}
        canSubmit={validation.canSubmit}
        blockers={validation.blockers}
        cartItems={cartItems}
        contacts={contacts}
        onUpdate={onUpdate}
      />
    </motion.div>
  );
}
